/*
 * All dashboard text in Albanian (sq) and English (en).
 * To change a word, edit it here. Missing Albanian text falls back to English.
 */
export type Lang = 'sq' | 'en';

const en: Record<string, string> = {
  // common
  'common.add': 'Add',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.choose': 'Choose…',
  'common.close': 'Close',
  'common.details': 'Details',
  'common.edit': 'Edit',
  'common.filtered': 'filter',
  'common.loading': 'Loading…',
  'common.none': 'None',
  'common.notes': 'Notes',
  'common.open': 'Open',
  'common.openMap': 'Map',
  'common.remove': 'Remove',
  'common.retry': 'Try again',
  'common.save': 'Save',
  'common.search': 'Search',
  'common.selectAll': 'Select all',
  'common.selectAllN': 'Select all ({n})',
  'common.shown': '{n} shown',
  'common.status': 'Status',
  'common.wait': 'Please wait…',

  // navigation
  'nav.main': 'Main menu',
  'nav.home': 'Home',
  'nav.events': 'Events',
  'nav.equipment': 'Equipment',
  'nav.kits': 'Cases & kits',
  'nav.timesheets': 'Working hours',
  'nav.crew': 'Staff',
  'nav.venues': 'Venues',
  'nav.clients': 'Clients',
  'nav.settings': 'Settings',
  'nav.logout': 'Log out',
  'nav.switchLanguage': 'Switch language',

  // login
  'login.title': 'Equipment, events and working hours',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Log in',
  'login.useApp': 'This dashboard is for the office. Please use the TC Desk app on your phone.',

  // roles
  'role.owner': 'Owner',
  'role.office': 'Office',
  'role.lead': 'Lead technician',
  'role.technician': 'Technician',
  'roleHint.owner': 'Full access, manages staff accounts',
  'roleHint.office': 'Events, equipment, staff schedule and working hours',
  'roleHint.lead': 'Phone app; can lead jobs and confirm load-out',
  'roleHint.technician': 'Phone app; own jobs and hours',
  'crewRole.lead': 'Lead',
  'crewRole.technician': 'Technician',

  // statuses
  'eventStatus.draft': 'Draft',
  'eventStatus.confirmed': 'Confirmed',
  'eventStatus.in_progress': 'In progress',
  'eventStatus.completed': 'Completed',
  'eventStatus.invoiced': 'Invoiced',
  'eventStatus.cancelled': 'Cancelled',
  'itemStatus.available': 'Available',
  'itemStatus.out': 'Out',
  'itemStatus.in_repair': 'In repair',
  'itemStatus.lost': 'Lost',
  'itemStatus.retired': 'Retired',
  'entryStatus.open': 'Clocked in',
  'entryStatus.submitted': 'To approve',
  'entryStatus.approved': 'Approved',
  'entryStatus.rejected': 'Rejected',
  'scan.loadout': 'Load-out',
  'scan.install': 'Installed',
  'scan.return': 'Returned',
  'packing.pending': 'Not loaded',
  'packing.out': 'Out',
  'packing.returned': 'Returned',

  // home
  'home.morning': 'Good morning',
  'home.hello': 'Hello',
  'home.jobsToday': 'Jobs today',
  'home.onSiteNow': 'On site now',
  'home.toApprove': 'Hours to approve',
  'home.unverified': '{n} need a closer look',
  'home.notReturned': 'Equipment not returned',
  'home.today': 'Today',
  'home.allEvents': 'All events',
  'home.noJobsToday': 'No jobs today.',
  'home.crewItems': '{crew} crew · {items} items',
  'home.nobodyOnSite': 'Nobody is clocked in right now.',
  'home.since': 'since {time}',
  'home.onBreak': 'On break',
  'home.verifiedOnSite': 'Verified on site',
  'home.needsAttention': 'Next 7 days: needs attention',
  'home.allReady': 'All upcoming jobs have crew and equipment.',
  'home.allReturned': 'Everything from finished jobs is back.',
  'home.itemsOut': '{n} still out',

  // events
  'events.title': 'Events',
  'events.sub': 'Jobs with their venue, crew and equipment',
  'events.new': 'New event',
  'events.edit': 'Edit event',
  'events.search': 'Search event, venue or client',
  'events.range': 'Time',
  'events.upcoming': 'Upcoming',
  'events.past': 'Past',
  'events.all': 'All',
  'events.date': 'Date',
  'events.event': 'Event',
  'events.eventName': 'Event name',
  'events.venue': 'Venue',
  'events.client': 'Client',
  'events.crew': 'Crew',
  'events.equipment': 'Items',
  'events.leadRequired': 'Lead required',
  'events.emptyTitle': 'No events here',
  'events.emptyText': 'Create an event to start booking equipment and crew.',
  'events.noVenues': 'Add a venue first, with its location on the map.',
  'events.setupStart': 'Setup starts',
  'events.setupStartHint': 'Changing this moves the other times too',
  'events.eventStart': 'Event starts',
  'events.eventEnd': 'Event ends',
  'events.teardownEnd': 'Teardown ends',
  'events.languages': 'Languages',
  'events.languagesHint': 'e.g. EN, SQ, SR',
  'events.delegates': 'Delegates',
  'events.requiresLead': 'This job needs a lead technician',
  'events.notesPlaceholder': 'Access, parking, power points, contacts on site…',
  'events.action.confirm': 'Confirm',
  'events.action.cancel': 'Cancel event',
  'events.action.complete': 'Mark completed',
  'events.action.toDraft': 'Back to draft',
  'events.action.invoiced': 'Mark invoiced',
  'events.action.reopen': 'Reopen',
  'events.action.restore': 'Restore as draft',
  'events.confirmCancel': 'Cancel this event? Its equipment becomes free for other events.',
  'events.tab.overview': 'Overview',
  'events.tab.equipment': 'Equipment',
  'events.tab.crew': 'Crew',
  'events.tab.hours': 'Hours',
  'events.tab.scans': 'Scans',
  'events.notReady': 'Not ready yet',
  'events.problem.draft': 'Draft',
  'events.problem.no_crew': 'No crew assigned',
  'events.problem.no_lead': 'No lead technician',
  'events.problem.no_equipment': 'No equipment booked',
  'events.schedule': 'Schedule',
  'events.venueAndClient': 'Venue and client',
  'events.loadoutConfirmed': 'Load-out confirmed {when}',
  'events.noCrew': 'No crew assigned yet',
  'events.equipmentHint': 'Book whole cases or single items. The crew scans them at load-out and return.',
  'events.bookEquipment': 'Book equipment',
  'events.bookHint': 'Only equipment that is free for the whole job time is shown.',
  'events.bookN': 'Book {cases} cases + {items} items',
  'events.singleItems': 'Single items',
  'events.nothingAvailable': 'Nothing available for these times.',
  'events.someUnavailable': 'Some selected items are no longer available.',
  'events.noEquipment': 'No equipment booked',
  'events.noEquipmentText': 'Book cases or items for this job.',
  'events.looseItems': 'Single items',
  'events.removeCase': 'Remove case',
  'events.crewHint': 'Technicians see this job in their app as soon as it is confirmed.',
  'events.crewHintLead': 'This job needs a lead technician before it can be confirmed.',
  'events.addCrew': 'Add to crew',
  'events.doubleBooked': '{name} is also booked on: {events}',
  'events.hoursHint': 'Clock-ins for this job, checked against the venue location.',
  'events.openInTimesheets': 'Open in working hours',
  'events.noScans': 'No scans yet',
  'events.noScansText': 'Scans appear here when the crew loads out and returns equipment.',
  'events.when': 'When',
  'events.scanType': 'Scan',
  'events.by': 'By',

  // equipment
  'equipment.title': 'Equipment',
  'equipment.sub': 'Every item has its own label. Scan it to load out and return.',
  'equipment.addItem': 'Add item',
  'equipment.editItem': 'Edit item',
  'equipment.addMany': 'Add many',
  'equipment.printSelected': 'Print {n} labels',
  'equipment.printShown': 'Print labels ({n})',
  'equipment.printLabel': 'Print label',
  'equipment.statTotal': 'Total',
  'equipment.statOut': 'Out on jobs',
  'equipment.statProblem': 'Lost or in repair',
  'equipment.search': 'Search name, code or serial',
  'equipment.allCategories': 'All categories',
  'equipment.allStatuses': 'All statuses',
  'equipment.code': 'Code',
  'equipment.name': 'Name',
  'equipment.category': 'Category',
  'equipment.brand': 'Brand',
  'equipment.model': 'Model',
  'equipment.brandModel': 'Brand / model',
  'equipment.serial': 'Serial no.',
  'equipment.case': 'Case',
  'equipment.location': 'Where',
  'equipment.warehouse': 'Warehouse',
  'equipment.outOn': 'Out on',
  'equipment.purchaseDate': 'Purchase date',
  'equipment.purchasePrice': 'Purchase price (€)',
  'equipment.codeHint': 'Leave empty to create a new label code. Fill in only if the item already has a TC label.',
  'equipment.emptyTitle': 'No equipment yet',
  'equipment.emptyText': 'Start with "Add many" to add, for example, 100 receivers at once.',
  'equipment.bulkHint': 'Each item gets its own label code. You can print the labels right after.',
  'equipment.howMany': 'How many',
  'equipment.createN': 'Create {n} items',
  'equipment.putInCase': 'Put into case',
  'equipment.putInCaseHint': 'Optional. You can also do this later.',
  'equipment.noCase': 'No case',
  'equipment.bulkDoneTitle': 'Items created',
  'equipment.bulkDone': '{n} × {name} created.',
  'equipment.bulkDoneHint': 'Print the labels now and stick one on each item.',
  'equipment.printTheseLabels': 'Print {n} labels',
  'equipment.changeStatus': 'Change status',
  'equipment.statusHint': 'Mark an item that is broken, lost or no longer used. It then cannot be booked.',
  'equipment.outHint': 'This item is out on a job. It comes back when the crew scans it at return.',
  'equipment.markAs.available': 'Back in use',
  'equipment.markAs.in_repair': 'In repair',
  'equipment.markAs.lost': 'Mark lost',
  'equipment.markAs.retired': 'Retire',
  'equipment.bookings': 'Bookings',
  'equipment.noBookings': 'Not booked on any event yet.',
  'equipment.scanHistory': 'Scan history',
  'equipment.noScans': 'Not scanned yet.',

  // kits
  'kits.title': 'Cases & kits',
  'kits.sub': 'Items that travel together. One scan of the case label covers everything inside.',
  'kits.new': 'New case',
  'kits.edit': 'Edit case',
  'kits.name': 'Name',
  'kits.nameHint': 'e.g. "IR case A – 50 receivers"',
  'kits.items': 'Items',
  'kits.emptyTitle': 'No cases yet',
  'kits.emptyText': 'Create a case, then add items to it.',
  'kits.printCaseLabels': 'Print case labels',
  'kits.printCaseLabel': 'Print case label',
  'kits.printItemLabels': 'Print item labels ({n})',
  'kits.addItems': 'Add items',
  'kits.addN': 'Add {n} items',
  'kits.noItems': 'This case is empty',
  'kits.noItemsText': 'Add the items that travel in this case.',
  'kits.removeFromCase': 'Take out',
  'kits.onlyFree': 'Only items that are not in another case are listed.',
  'kits.noFreeItems': 'No free items found.',

  // labels
  'labels.title': 'Labels ({n})',
  'labels.print': 'Print',
  'labels.size': 'Label size',
  'labels.sizeSmall': 'Small – 4 per row',
  'labels.sizeMedium': 'Medium – 3 per row',
  'labels.sizeLarge': 'Large – 2 per row',
  'labels.copies': 'Copies',
  'labels.hint': 'Prints on A4. Use sticker sheets or plain paper and cut. Set the printer to 100% scale.',
  'labels.none': 'No labels chosen. Go back and select items.',

  // timesheets
  'timesheets.title': 'Working hours',
  'timesheets.sub': 'Clock-ins are only possible on site. Check and approve them here.',
  'timesheets.export': 'Excel',
  'timesheets.approveVerified': 'Approve {n} verified',
  'timesheets.filteredByEvent': 'Showing hours for one event.',
  'timesheets.clearFilter': 'Show all',
  'timesheets.prevWeek': 'Previous week',
  'timesheets.nextWeek': 'Next week',
  'timesheets.from': 'From',
  'timesheets.to': 'To',
  'timesheets.everyone': 'Everyone',
  'timesheets.technician': 'Technician',
  'timesheets.in': 'In',
  'timesheets.out': 'Out',
  'timesheets.break': 'Break',
  'timesheets.breakMinutes': 'Break (minutes)',
  'timesheets.hours': 'Hours',
  'timesheets.total': 'Total',
  'timesheets.location': 'Location',
  'timesheets.locationCheck': 'Location check',
  'timesheets.manual': 'Manual entry',
  'timesheets.onSite': 'On site',
  'timesheets.approve': 'Approve',
  'timesheets.reject': 'Reject',
  'timesheets.approvedHours': '{h} approved',
  'timesheets.empty': 'No hours in this period',
  'timesheets.emptyText': 'Hours appear when technicians clock in on site with the app.',
  'timesheets.rejectTitle': 'Reject hours – {name}',
  'timesheets.rejectReason': 'Reason',
  'timesheets.rejectHint': 'The technician sees this reason.',
  'timesheets.editTitle': 'Correct hours – {name}',
  'timesheets.editWarning': 'Corrections are saved in the history with your name and reason.',
  'timesheets.editReason': 'Reason for the correction',

  // staff
  'crew.title': 'Staff',
  'crew.sub': '{leads} lead technicians · {techs} technicians',
  'crew.new': 'New person',
  'crew.name': 'Name',
  'crew.role': 'Role',
  'crew.certifiedSystems': 'Certified on',
  'crew.showInactive': 'Show inactive',
  'crew.inactive': 'Inactive',
  'crew.empty': 'No staff yet',
  'crew.emailFixed': 'Used to log in; cannot be changed',
  'crew.newPassword': 'New password',
  'crew.newPasswordHint': 'Leave empty to keep. A new password logs them out on all devices.',
  'crew.passwordHint': 'At least 8 characters',
  'crew.activeLabel': 'Active (can log in)',
  'crew.certifications': 'Certified systems and training',
  'crew.noCerts': 'None recorded yet.',
  'crew.system': 'System',
  'crew.certifiedOn': 'Date',
  'crew.trainer': 'Trainer',
  'crew.person': 'Person',
  'crew.roleOnJob': 'Role on this job',
  'crew.certified': 'Certified on',

  // venues
  'venues.title': 'Venues',
  'venues.sub': 'Clock-in works only inside the circle around the venue.',
  'venues.new': 'New venue',
  'venues.edit': 'Edit venue',
  'venues.name': 'Name',
  'venues.address': 'Address',
  'venues.geofence': 'Clock-in radius',
  'venues.geofenceHint': '200 m suits most venues. Make it larger for big hotels or conference centres.',
  'venues.location': 'Location',
  'venues.pasteLocation': 'Paste location (optional)',
  'venues.pasteHint': 'Coordinates or a Google Maps link. Or click the map below.',
  'venues.pasteError': 'Could not read a location from this. Click the map instead.',
  'venues.clickMap': 'Click the venue entrance on the map',
  'venues.notesHint': 'Loading bay, parking, power points, contact on site…',
  'venues.emptyTitle': 'No venues yet',
  'venues.emptyText': 'Add the places where you install equipment.',

  // clients
  'clients.title': 'Clients',
  'clients.new': 'New client',
  'clients.edit': 'Edit client',
  'clients.name': 'Name',
  'clients.contact': 'Contact person',
  'clients.phone': 'Phone',
  'clients.email': 'Email',
  'clients.vat': 'VAT / fiscal no.',
  'clients.currency': 'Currency',
  'clients.emptyTitle': 'No clients yet',

  // settings
  'settings.title': 'Settings',
  'settings.profile': 'My profile',
  'settings.language': 'Language',
  'settings.changePassword': 'Change password',
  'settings.currentPassword': 'Current password',
  'settings.newPassword': 'New password',
  'settings.repeatPassword': 'Repeat new password',
  'settings.mismatch': 'The passwords do not match',
  'settings.passwordChanged': 'Password changed. Other devices have been logged out.',

  // errors from the server
  'err.network': 'No connection to the server. Check the internet and try again.',
  'err.invalid_login': 'Email or password is incorrect.',
  'err.unauthorized': 'Please log in again.',
  'err.forbidden': 'You are not allowed to do this.',
  'err.too_many_requests': 'Too many attempts. Wait a minute and try again.',
  'err.validation_error': 'Some fields are missing or invalid:',
  'err.not_found': 'Not found. It may have been removed.',
  'err.duplicate': 'This already exists (same code or email).',
  'err.email_taken': 'A person with this email already exists.',
  'err.invalid_times': 'The times must run in order: setup → event start → event end → teardown.',
  'err.item_conflict': 'Some equipment is already booked for another event at that time:',
  'err.items_unavailable': 'Some items are lost, in repair or retired:',
  'err.item_out': 'This item is out on the job. It must be scanned back first.',
  'err.item_in_other_kit': 'Some items are already in another case:',
  'err.lead_required': 'Assign a lead technician before confirming this event.',
  'err.not_a_lead': 'This person is not a lead technician.',
  'err.event_closed': 'This event is closed and cannot be changed.',
  'err.items_still_out': 'Equipment is still out on this event. It must be returned first.',
  'err.invalid_status_change': 'This status change is not possible.',
  'err.note_required': 'Please give a reason.',
  'err.still_open': 'This person is still clocked in.',
  'err.use_scanning': 'Items go out only by scanning them at load-out.',
  'err.wrong_password': 'The current password is incorrect.',
  'err.cannot_demote_self': 'You cannot deactivate yourself or remove your own owner role.',
  'err.server_error': 'Something went wrong on the server. Try again.',
};

const sq: Record<string, string> = {
  'common.add': 'Shto',
  'common.back': 'Kthehu',
  'common.cancel': 'Anulo',
  'common.choose': 'Zgjidh…',
  'common.close': 'Mbyll',
  'common.details': 'Detajet',
  'common.edit': 'Ndrysho',
  'common.filtered': 'filtër',
  'common.loading': 'Duke u ngarkuar…',
  'common.none': 'Asnjë',
  'common.notes': 'Shënime',
  'common.open': 'Hap',
  'common.openMap': 'Harta',
  'common.remove': 'Hiq',
  'common.retry': 'Provo sërish',
  'common.save': 'Ruaj',
  'common.search': 'Kërko',
  'common.selectAll': 'Zgjidh të gjitha',
  'common.selectAllN': 'Zgjidh të gjitha ({n})',
  'common.shown': '{n} të shfaqura',
  'common.status': 'Statusi',
  'common.wait': 'Ju lutem prisni…',

  'nav.main': 'Menyja kryesore',
  'nav.home': 'Ballina',
  'nav.events': 'Eventet',
  'nav.equipment': 'Pajisjet',
  'nav.kits': 'Kutitë dhe setet',
  'nav.timesheets': 'Orët e punës',
  'nav.crew': 'Stafi',
  'nav.venues': 'Lokacionet',
  'nav.clients': 'Klientët',
  'nav.settings': 'Cilësimet',
  'nav.logout': 'Dil',
  'nav.switchLanguage': 'Ndrysho gjuhën',

  'login.title': 'Pajisjet, eventet dhe orët e punës',
  'login.email': 'Email',
  'login.password': 'Fjalëkalimi',
  'login.submit': 'Kyçu',
  'login.useApp': 'Ky panel është për zyrën. Ju lutem përdorni aplikacionin TC Desk në telefon.',

  'role.owner': 'Pronar',
  'role.office': 'Zyra',
  'role.lead': 'Teknik kryesor',
  'role.technician': 'Teknik',
  'roleHint.owner': 'Qasje e plotë, menaxhon llogaritë e stafit',
  'roleHint.office': 'Eventet, pajisjet, orari i stafit dhe orët e punës',
  'roleHint.lead': 'Aplikacioni në telefon; udhëheq punët dhe konfirmon ngarkimin',
  'roleHint.technician': 'Aplikacioni në telefon; punët dhe orët e veta',
  'crewRole.lead': 'Kryesor',
  'crewRole.technician': 'Teknik',

  'eventStatus.draft': 'Skicë',
  'eventStatus.confirmed': 'Konfirmuar',
  'eventStatus.in_progress': 'Në zhvillim',
  'eventStatus.completed': 'Përfunduar',
  'eventStatus.invoiced': 'Faturuar',
  'eventStatus.cancelled': 'Anuluar',
  'itemStatus.available': 'Në dispozicion',
  'itemStatus.out': 'Në terren',
  'itemStatus.in_repair': 'Në riparim',
  'itemStatus.lost': 'Humbur',
  'itemStatus.retired': 'Jashtë përdorimit',
  'entryStatus.open': 'Në punë',
  'entryStatus.submitted': 'Për miratim',
  'entryStatus.approved': 'Miratuar',
  'entryStatus.rejected': 'Refuzuar',
  'scan.loadout': 'Ngarkim',
  'scan.install': 'Instaluar',
  'scan.return': 'Kthyer',
  'packing.pending': 'Pa ngarkuar',
  'packing.out': 'Në terren',
  'packing.returned': 'Kthyer',

  'home.morning': 'Mirëmëngjes',
  'home.hello': 'Përshëndetje',
  'home.jobsToday': 'Punët sot',
  'home.onSiteNow': 'Në terren tani',
  'home.toApprove': 'Orë për miratim',
  'home.unverified': '{n} duhen kontrolluar',
  'home.notReturned': 'Pajisje të pakthyera',
  'home.today': 'Sot',
  'home.allEvents': 'Të gjitha eventet',
  'home.noJobsToday': 'Nuk ka punë sot.',
  'home.crewItems': '{crew} në ekip · {items} pajisje',
  'home.nobodyOnSite': 'Askush nuk është i regjistruar në punë tani.',
  'home.since': 'nga {time}',
  'home.onBreak': 'Në pushim',
  'home.verifiedOnSite': 'Verifikuar në lokacion',
  'home.needsAttention': '7 ditët e ardhshme: kërkojnë vëmendje',
  'home.allReady': 'Të gjitha punët e ardhshme kanë ekip dhe pajisje.',
  'home.allReturned': 'Gjithçka nga punët e përfunduara është kthyer.',
  'home.itemsOut': '{n} ende jashtë',

  'events.title': 'Eventet',
  'events.sub': 'Punët me lokacionin, ekipin dhe pajisjet',
  'events.new': 'Event i ri',
  'events.edit': 'Ndrysho eventin',
  'events.search': 'Kërko eventin, lokacionin ose klientin',
  'events.range': 'Koha',
  'events.upcoming': 'Të ardhshme',
  'events.past': 'Të kaluara',
  'events.all': 'Të gjitha',
  'events.date': 'Data',
  'events.event': 'Eventi',
  'events.eventName': 'Emri i eventit',
  'events.venue': 'Lokacioni',
  'events.client': 'Klienti',
  'events.crew': 'Ekipi',
  'events.equipment': 'Pajisje',
  'events.leadRequired': 'Kërkon teknik kryesor',
  'events.emptyTitle': 'Nuk ka evente këtu',
  'events.emptyText': 'Krijoni një event për të rezervuar pajisjet dhe ekipin.',
  'events.noVenues': 'Shtoni fillimisht një lokacion, me vendndodhjen në hartë.',
  'events.setupStart': 'Fillimi i montimit',
  'events.setupStartHint': 'Kur e ndryshoni, zhvendosen edhe oraret tjera',
  'events.eventStart': 'Fillimi i eventit',
  'events.eventEnd': 'Përfundimi i eventit',
  'events.teardownEnd': 'Përfundimi i çmontimit',
  'events.languages': 'Gjuhët',
  'events.languagesHint': 'p.sh. EN, SQ, SR',
  'events.delegates': 'Delegatët',
  'events.requiresLead': 'Kjo punë kërkon teknik kryesor',
  'events.notesPlaceholder': 'Hyrja, parkingu, rrymat, kontaktet në lokacion…',
  'events.action.confirm': 'Konfirmo',
  'events.action.cancel': 'Anulo eventin',
  'events.action.complete': 'Shëno si të përfunduar',
  'events.action.toDraft': 'Kthe në skicë',
  'events.action.invoiced': 'Shëno si të faturuar',
  'events.action.reopen': 'Rihap',
  'events.action.restore': 'Rikthe si skicë',
  'events.confirmCancel': 'Ta anuloni këtë event? Pajisjet e tij lirohen për evente tjera.',
  'events.tab.overview': 'Përmbledhje',
  'events.tab.equipment': 'Pajisjet',
  'events.tab.crew': 'Ekipi',
  'events.tab.hours': 'Orët',
  'events.tab.scans': 'Skanimet',
  'events.notReady': 'Ende jo gati',
  'events.problem.draft': 'Skicë',
  'events.problem.no_crew': 'Pa ekip',
  'events.problem.no_lead': 'Pa teknik kryesor',
  'events.problem.no_equipment': 'Pa pajisje të rezervuara',
  'events.schedule': 'Orari',
  'events.venueAndClient': 'Lokacioni dhe klienti',
  'events.loadoutConfirmed': 'Ngarkimi u konfirmua {when}',
  'events.noCrew': 'Ende pa ekip',
  'events.equipmentHint': 'Rezervoni kuti të plota ose pajisje të veçanta. Ekipi i skanon gjatë ngarkimit dhe kthimit.',
  'events.bookEquipment': 'Rezervo pajisje',
  'events.bookHint': 'Shfaqen vetëm pajisjet që janë të lira për gjithë kohën e punës.',
  'events.bookN': 'Rezervo {cases} kuti + {items} pajisje',
  'events.singleItems': 'Pajisje të veçanta',
  'events.nothingAvailable': 'Nuk ka asgjë të lirë për këto orare.',
  'events.someUnavailable': 'Disa pajisje të zgjedhura nuk janë më të lira.',
  'events.noEquipment': 'Pa pajisje të rezervuara',
  'events.noEquipmentText': 'Rezervoni kuti ose pajisje për këtë punë.',
  'events.looseItems': 'Pajisje të veçanta',
  'events.removeCase': 'Hiq kutinë',
  'events.crewHint': 'Teknikët e shohin këtë punë në aplikacion sapo të konfirmohet.',
  'events.crewHintLead': 'Kjo punë kërkon teknik kryesor para se të konfirmohet.',
  'events.addCrew': 'Shto në ekip',
  'events.doubleBooked': '{name} është i caktuar edhe në: {events}',
  'events.hoursHint': 'Regjistrimet e hyrjes për këtë punë, të kontrolluara me vendndodhjen e lokacionit.',
  'events.openInTimesheets': 'Hap te orët e punës',
  'events.noScans': 'Ende pa skanime',
  'events.noScansText': 'Skanimet shfaqen këtu kur ekipi ngarkon dhe kthen pajisjet.',
  'events.when': 'Kur',
  'events.scanType': 'Skanimi',
  'events.by': 'Nga',

  'equipment.title': 'Pajisjet',
  'equipment.sub': 'Çdo pajisje ka etiketën e vet. Skanojeni për ngarkim dhe kthim.',
  'equipment.addItem': 'Shto pajisje',
  'equipment.editItem': 'Ndrysho pajisjen',
  'equipment.addMany': 'Shto shumë',
  'equipment.printSelected': 'Printo {n} etiketa',
  'equipment.printShown': 'Printo etiketat ({n})',
  'equipment.printLabel': 'Printo etiketën',
  'equipment.statTotal': 'Gjithsej',
  'equipment.statOut': 'Në terren',
  'equipment.statProblem': 'Të humbura ose në riparim',
  'equipment.search': 'Kërko emrin, kodin ose numrin serik',
  'equipment.allCategories': 'Të gjitha kategoritë',
  'equipment.allStatuses': 'Të gjitha statuset',
  'equipment.code': 'Kodi',
  'equipment.name': 'Emri',
  'equipment.category': 'Kategoria',
  'equipment.brand': 'Prodhuesi',
  'equipment.model': 'Modeli',
  'equipment.brandModel': 'Prodhuesi / modeli',
  'equipment.serial': 'Nr. serik',
  'equipment.case': 'Kutia',
  'equipment.location': 'Ku ndodhet',
  'equipment.warehouse': 'Depo',
  'equipment.outOn': 'Në terren te',
  'equipment.purchaseDate': 'Data e blerjes',
  'equipment.purchasePrice': 'Çmimi i blerjes (€)',
  'equipment.codeHint': 'Lëreni bosh për të krijuar kod të ri. Plotësojeni vetëm nëse pajisja ka tashmë etiketë TC.',
  'equipment.emptyTitle': 'Ende pa pajisje',
  'equipment.emptyText': 'Filloni me "Shto shumë" për të shtuar, p.sh., 100 marrës njëherësh.',
  'equipment.bulkHint': 'Çdo pajisje merr kodin e vet. Etiketat mund t\'i printoni menjëherë pas.',
  'equipment.howMany': 'Sa copë',
  'equipment.createN': 'Krijo {n} pajisje',
  'equipment.putInCase': 'Vendos në kuti',
  'equipment.putInCaseHint': 'Opsionale. Mund ta bëni edhe më vonë.',
  'equipment.noCase': 'Pa kuti',
  'equipment.bulkDoneTitle': 'Pajisjet u krijuan',
  'equipment.bulkDone': 'U krijuan {n} × {name}.',
  'equipment.bulkDoneHint': 'Printoni etiketat tani dhe ngjitni një në secilën pajisje.',
  'equipment.printTheseLabels': 'Printo {n} etiketa',
  'equipment.changeStatus': 'Ndrysho statusin',
  'equipment.statusHint': 'Shënoni pajisjen e prishur, të humbur ose që nuk përdoret më. Pastaj nuk mund të rezervohet.',
  'equipment.outHint': 'Kjo pajisje është në terren. Kthehet kur ekipi e skanon gjatë kthimit.',
  'equipment.markAs.available': 'Rikthe në përdorim',
  'equipment.markAs.in_repair': 'Në riparim',
  'equipment.markAs.lost': 'Shëno të humbur',
  'equipment.markAs.retired': 'Nxirr nga përdorimi',
  'equipment.bookings': 'Rezervimet',
  'equipment.noBookings': 'Ende e parezervuar në asnjë event.',
  'equipment.scanHistory': 'Historiku i skanimeve',
  'equipment.noScans': 'Ende e paskanuar.',

  'kits.title': 'Kutitë dhe setet',
  'kits.sub': 'Pajisje që udhëtojnë bashkë. Një skanim i etiketës së kutisë mbulon gjithçka brenda.',
  'kits.new': 'Kuti e re',
  'kits.edit': 'Ndrysho kutinë',
  'kits.name': 'Emri',
  'kits.nameHint': 'p.sh. "Kutia IR A – 50 marrës"',
  'kits.items': 'Pajisje',
  'kits.emptyTitle': 'Ende pa kuti',
  'kits.emptyText': 'Krijoni një kuti, pastaj shtoni pajisjet në të.',
  'kits.printCaseLabels': 'Printo etiketat e kutive',
  'kits.printCaseLabel': 'Printo etiketën e kutisë',
  'kits.printItemLabels': 'Printo etiketat e pajisjeve ({n})',
  'kits.addItems': 'Shto pajisje',
  'kits.addN': 'Shto {n} pajisje',
  'kits.noItems': 'Kjo kuti është bosh',
  'kits.noItemsText': 'Shtoni pajisjet që udhëtojnë në këtë kuti.',
  'kits.removeFromCase': 'Nxirre',
  'kits.onlyFree': 'Shfaqen vetëm pajisjet që nuk janë në kuti tjetër.',
  'kits.noFreeItems': 'Nuk u gjetën pajisje të lira.',

  'labels.title': 'Etiketat ({n})',
  'labels.print': 'Printo',
  'labels.size': 'Madhësia e etiketës',
  'labels.sizeSmall': 'E vogël – 4 në rresht',
  'labels.sizeMedium': 'Mesatare – 3 në rresht',
  'labels.sizeLarge': 'E madhe – 2 në rresht',
  'labels.copies': 'Kopje',
  'labels.hint': 'Printohet në A4. Përdorni fletë ngjitëse ose letër të thjeshtë dhe prijeni. Vendoseni printerin në shkallë 100%.',
  'labels.none': 'Nuk është zgjedhur asnjë etiketë. Kthehuni dhe zgjidhni pajisjet.',

  'timesheets.title': 'Orët e punës',
  'timesheets.sub': 'Regjistrimi i hyrjes është i mundur vetëm në lokacion. Kontrollojini dhe miratojini këtu.',
  'timesheets.export': 'Excel',
  'timesheets.approveVerified': 'Mirato {n} të verifikuara',
  'timesheets.filteredByEvent': 'Po shfaqen orët për një event.',
  'timesheets.clearFilter': 'Shfaq të gjitha',
  'timesheets.prevWeek': 'Java e kaluar',
  'timesheets.nextWeek': 'Java e ardhshme',
  'timesheets.from': 'Nga',
  'timesheets.to': 'Deri',
  'timesheets.everyone': 'Të gjithë',
  'timesheets.technician': 'Tekniku',
  'timesheets.in': 'Hyrja',
  'timesheets.out': 'Dalja',
  'timesheets.break': 'Pushimi',
  'timesheets.breakMinutes': 'Pushimi (minuta)',
  'timesheets.hours': 'Orë',
  'timesheets.total': 'Gjithsej',
  'timesheets.location': 'Vendndodhja',
  'timesheets.locationCheck': 'Kontrolli i vendndodhjes',
  'timesheets.manual': 'Regjistrim manual',
  'timesheets.onSite': 'Në lokacion',
  'timesheets.approve': 'Mirato',
  'timesheets.reject': 'Refuzo',
  'timesheets.approvedHours': '{h} të miratuara',
  'timesheets.empty': 'Nuk ka orë në këtë periudhë',
  'timesheets.emptyText': 'Orët shfaqen kur teknikët regjistrojnë hyrjen në lokacion me aplikacion.',
  'timesheets.rejectTitle': 'Refuzo orët – {name}',
  'timesheets.rejectReason': 'Arsyeja',
  'timesheets.rejectHint': 'Tekniku e sheh këtë arsye.',
  'timesheets.editTitle': 'Korrigjo orët – {name}',
  'timesheets.editWarning': 'Korrigjimet ruhen në histori me emrin dhe arsyen tuaj.',
  'timesheets.editReason': 'Arsyeja e korrigjimit',

  'crew.title': 'Stafi',
  'crew.sub': '{leads} teknikë kryesorë · {techs} teknikë',
  'crew.new': 'Person i ri',
  'crew.name': 'Emri',
  'crew.role': 'Roli',
  'crew.certifiedSystems': 'I certifikuar për',
  'crew.showInactive': 'Shfaq joaktivët',
  'crew.inactive': 'Joaktiv',
  'crew.empty': 'Ende pa staf',
  'crew.emailFixed': 'Përdoret për kyçje; nuk mund të ndryshohet',
  'crew.newPassword': 'Fjalëkalim i ri',
  'crew.newPasswordHint': 'Lëreni bosh për ta mbajtur. Fjalëkalimi i ri e çkyç personin nga të gjitha pajisjet.',
  'crew.passwordHint': 'Së paku 8 karaktere',
  'crew.activeLabel': 'Aktiv (mund të kyçet)',
  'crew.certifications': 'Sistemet e certifikuara dhe trajnimet',
  'crew.noCerts': 'Ende asgjë e regjistruar.',
  'crew.system': 'Sistemi',
  'crew.certifiedOn': 'Data',
  'crew.trainer': 'Trajneri',
  'crew.person': 'Personi',
  'crew.roleOnJob': 'Roli në këtë punë',
  'crew.certified': 'I certifikuar për',

  'venues.title': 'Lokacionet',
  'venues.sub': 'Regjistrimi i hyrjes funksionon vetëm brenda rrethit rreth lokacionit.',
  'venues.new': 'Lokacion i ri',
  'venues.edit': 'Ndrysho lokacionin',
  'venues.name': 'Emri',
  'venues.address': 'Adresa',
  'venues.geofence': 'Rrezja e regjistrimit',
  'venues.geofenceHint': '200 m mjafton për shumicën e lokacioneve. Rriteni për hotele ose qendra të mëdha konferencash.',
  'venues.location': 'Vendndodhja',
  'venues.pasteLocation': 'Ngjit vendndodhjen (opsionale)',
  'venues.pasteHint': 'Koordinatat ose një link nga Google Maps. Ose klikoni në hartë më poshtë.',
  'venues.pasteError': 'Nuk u lexua vendndodhja. Klikoni në hartë.',
  'venues.clickMap': 'Klikoni hyrjen e lokacionit në hartë',
  'venues.notesHint': 'Hyrja për ngarkim, parkingu, rrymat, kontakti në lokacion…',
  'venues.emptyTitle': 'Ende pa lokacione',
  'venues.emptyText': 'Shtoni vendet ku instaloni pajisjet.',

  'clients.title': 'Klientët',
  'clients.new': 'Klient i ri',
  'clients.edit': 'Ndrysho klientin',
  'clients.name': 'Emri',
  'clients.contact': 'Personi kontaktues',
  'clients.phone': 'Telefoni',
  'clients.email': 'Email',
  'clients.vat': 'Nr. fiskal / TVSH',
  'clients.currency': 'Valuta',
  'clients.emptyTitle': 'Ende pa klientë',

  'settings.title': 'Cilësimet',
  'settings.profile': 'Profili im',
  'settings.language': 'Gjuha',
  'settings.changePassword': 'Ndrysho fjalëkalimin',
  'settings.currentPassword': 'Fjalëkalimi aktual',
  'settings.newPassword': 'Fjalëkalimi i ri',
  'settings.repeatPassword': 'Përsërit fjalëkalimin e ri',
  'settings.mismatch': 'Fjalëkalimet nuk përputhen',
  'settings.passwordChanged': 'Fjalëkalimi u ndryshua. Pajisjet tjera u çkyçën.',

  'err.network': 'Nuk ka lidhje me serverin. Kontrolloni internetin dhe provoni sërish.',
  'err.invalid_login': 'Email-i ose fjalëkalimi është i pasaktë.',
  'err.unauthorized': 'Ju lutem kyçuni sërish.',
  'err.forbidden': 'Nuk keni leje për këtë veprim.',
  'err.too_many_requests': 'Shumë tentime. Prisni një minutë dhe provoni sërish.',
  'err.validation_error': 'Disa fusha mungojnë ose janë të pasakta:',
  'err.not_found': 'Nuk u gjet. Mund të jetë fshirë.',
  'err.duplicate': 'Kjo ekziston tashmë (i njëjti kod ose email).',
  'err.email_taken': 'Ekziston tashmë një person me këtë email.',
  'err.invalid_times': 'Oraret duhet të jenë me radhë: montimi → fillimi → përfundimi → çmontimi.',
  'err.item_conflict': 'Disa pajisje janë tashmë të rezervuara për një event tjetër në atë kohë:',
  'err.items_unavailable': 'Disa pajisje janë të humbura, në riparim ose jashtë përdorimit:',
  'err.item_out': 'Kjo pajisje është në terren. Duhet të skanohet fillimisht si e kthyer.',
  'err.item_in_other_kit': 'Disa pajisje janë tashmë në një kuti tjetër:',
  'err.lead_required': 'Caktoni një teknik kryesor para se ta konfirmoni eventin.',
  'err.not_a_lead': 'Ky person nuk është teknik kryesor.',
  'err.event_closed': 'Ky event është i mbyllur dhe nuk mund të ndryshohet.',
  'err.items_still_out': 'Ka ende pajisje në terren për këtë event. Duhet të kthehen fillimisht.',
  'err.invalid_status_change': 'Ky ndryshim i statusit nuk është i mundur.',
  'err.note_required': 'Ju lutem shkruani arsyen.',
  'err.still_open': 'Ky person është ende i regjistruar në punë.',
  'err.use_scanning': 'Pajisjet dalin në terren vetëm duke i skanuar gjatë ngarkimit.',
  'err.wrong_password': 'Fjalëkalimi aktual është i pasaktë.',
  'err.cannot_demote_self': 'Nuk mund ta çaktivizoni veten ose ta hiqni rolin tuaj të pronarit.',
  'err.server_error': 'Diçka shkoi keq në server. Provoni sërish.',
};

export const strings: Record<Lang, Record<string, string>> = { en, sq };
